import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import bookingsApi from "@/api/bookingsApi";
import BookingResponse from "@/model/BookingResponse";
import isoDate from "@/utils/isoDate";
import { formatMoney } from "@/utils/pricing";
import "@/pages/BookingConfirmationPage/BookingConfirmationPage.scss";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 30;

/**
 * Where Stripe sends the guest back after paying. Only the backend (via Stripe's webhook) can
 * confirm the booking, so this page polls it until the payment has been recorded.
 */
function BookingConfirmationPage() {
  const { t, i18n } = useTranslation();
  const { session } = useAuth();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  // Set by Stripe on the return URL; "failed" means the payment was declined after a redirect.
  const paymentFailed = searchParams.get("redirect_status") === "failed";

  const [booking, setBooking] = useState<BookingResponse | null>(null);
  const [error, setError] = useState(false);
  const [gaveUp, setGaveUp] = useState(false);

  useEffect(() => {
    if (!session || !id || paymentFailed) {
      return;
    }
    let cancelled = false;
    let polls = 0;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      try {
        const current = await bookingsApi.getBooking(session.accessToken, id);
        if (cancelled) {
          return;
        }
        setBooking(current);
        polls += 1;
        if (current.status === "PENDING_PAYMENT") {
          if (polls < MAX_POLLS) {
            timer = setTimeout(poll, POLL_INTERVAL_MS);
          } else {
            setGaveUp(true);
          }
        }
      } catch {
        if (!cancelled) {
          setError(true);
        }
      }
    };
    poll();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [session, id, paymentFailed]);

  if (!session || !id) {
    return null;
  }

  const renderState = () => {
    if (paymentFailed) {
      return (
        <>
          <h1>{t("confirmation.failedTitle")}</h1>
          <p>{t("confirmation.failedBody")}</p>
          <Link
            className="booking-confirmation-page__cta"
            to={`/trips/${id}/pay`}
          >
            {t("confirmation.tryAgain")}
          </Link>
        </>
      );
    }
    if (error) {
      return <p role="alert">{t("confirmation.loadError")}</p>;
    }
    if (!booking || booking.status === "PENDING_PAYMENT") {
      return (
        <>
          <span
            className="booking-confirmation-page__spinner"
            aria-hidden="true"
          />
          <h1>{t("confirmation.processingTitle")}</h1>
          <p>
            {gaveUp
              ? t("confirmation.stillProcessing")
              : t("confirmation.processingBody")}
          </p>
          {gaveUp && <Link to="/trips">{t("confirmation.toTrips")}</Link>}
        </>
      );
    }
    if (booking.status === "CONFIRMED") {
      return (
        <>
          <span className="booking-confirmation-page__check" aria-hidden="true">
            ✓
          </span>
          <p className="eyebrow">{t("confirmation.eyebrow")}</p>
          <h1>{t("confirmation.confirmedTitle")}</h1>
          <p>
            {t("confirmation.confirmedBody", {
              property: booking.propertyTitle,
              checkIn: isoDate.formatIsoDate(booking.checkIn, i18n.language),
              checkOut: isoDate.formatIsoDate(booking.checkOut, i18n.language),
              total: formatMoney(
                booking.totalAmount,
                booking.currency,
                i18n.language
              ),
            })}
          </p>
          <div className="booking-confirmation-page__actions">
            <Link className="booking-confirmation-page__cta" to="/trips">
              {t("confirmation.toTrips")}
            </Link>
            {booking.receiptUrl && (
              <a href={booking.receiptUrl} target="_blank" rel="noreferrer">
                {t("trips.receipt")}
              </a>
            )}
          </div>
        </>
      );
    }
    if (booking.status === "AWAITING_HOST") {
      return (
        <>
          <span className="booking-confirmation-page__check" aria-hidden="true">
            ✓
          </span>
          <p className="eyebrow">{t("confirmation.requestEyebrow")}</p>
          <h1>{t("confirmation.requestTitle")}</h1>
          <p>
            {t("confirmation.requestBody", {
              property: booking.propertyTitle,
              total: formatMoney(booking.totalAmount, booking.currency, i18n.language),
              deadline: booking.requestExpiresAt
                ? new Date(booking.requestExpiresAt).toLocaleString(i18n.language, {
                    weekday: "short",
                    day: "numeric",
                    month: "long",
                    hour: "2-digit",
                    minute: "2-digit",
                  })
                : "",
            })}
          </p>
          <div className="booking-confirmation-page__actions">
            <Link className="booking-confirmation-page__cta" to="/trips">
              {t("confirmation.toTrips")}
            </Link>
          </div>
        </>
      );
    }
    if (booking.status === "DECLINED") {
      return (
        <>
          <h1>{t("confirmation.declinedTitle")}</h1>
          <p>{t(`trips.declineReason.${booking.declineReason ?? "HOST_DECLINED"}`)}</p>
          <Link to="/trips">{t("confirmation.toTrips")}</Link>
        </>
      );
    }
    // CANCELLED or EXPIRED: e.g. the hold ran out and the nights went to someone else (refunded).
    return (
      <>
        <h1>{t("confirmation.notBookedTitle")}</h1>
        <p>{t("confirmation.notBookedBody")}</p>
        <Link to="/trips">{t("confirmation.toTrips")}</Link>
      </>
    );
  };

  return (
    <div className="booking-confirmation-page">
      <div className="booking-confirmation-page__card" role="status">
        {renderState()}
      </div>
    </div>
  );
}

export default BookingConfirmationPage;
